// "Run cycle" — the START control for the investment-reporting pipeline, and the last
// missing link in it.
//
// Everything downstream was already wired: reporting-cycle-worker is deployed (v1,
// verify_jwt=true, in-body service-role gate) and advances a cycle ONE stage per call;
// research-job-runner routes job_type `run_reporting_cycle` to it. What never existed was
// anything that created the first job. Golden CSVs landed in reporting_golden_rows and the
// cycle sat at `snapshot` indefinitely. This control writes that one queue row.
//
// WHY A CONFIRM STEP AND NOT A BUTTON
// This SPENDS MONEY. A run walks snapshot -> compute -> verify -> render -> gate -> deliver,
// and the compute, LLM-narrative and deck/newsletter render legs all bill. A bare button next
// to five read-only panels is a mis-click waiting to happen, and the cost is invisible until
// the invoice. So the dialog states, in plain words, exactly what the machine will do and
// where it stops — and requires an explicit acknowledgement, mirroring the GateTab
// "attempt anyway" checkbox pattern rather than inventing a new one.
//
// WHAT IT HONESTLY CANNOT DO, and says so: the run PARKS at the gate. runGate returns
// AWAITING_GATE unless cycle.gate_approved is already true, so nothing is ever delivered
// without a human signature on the Gate tab. Newsletter auto-send is additionally behind a
// disabled-by-default flag. Stating that is what makes the confirm honest rather than
// alarming.
//
// THREE CONTROLS, NOT ONE, AND THEY DO DIFFERENT THINGS
//   Run     the SPEND act. Queues one walk of the pipeline.
//   Reopen  0003p. Clears a terminal 'failed' and RESUMES at the stage that died. Every
//           completion marker survives, which is right when nothing about the inputs changed.
//   Restart 0003q. Rewinds the cursor to a chosen stage and DISCARDS the completion markers
//           behind it, so those stages derive again. Offered for every status except delivered,
//           which is deliberately wider than Reopen: a cycle that finished a stage successfully
//           but on the wrong prose or the wrong numbers had no control at all before this, and
//           the only way to make it re-derive was direct DML, which leaves the permanent audit
//           record describing a run that never happened.
// They are adjacent on purpose. All three read the same server-truth in-flight state, so none
// of them can be used while a tick is live.
//
// STATE IS READ FROM THE SERVER, NOT REMEMBERED
// The in-flight banner comes from get_reporting_cycle_run, not from local mutation state, so
// a reload — or a second reviewer on another laptop — sees the same truth. That matters
// because the whole point of the idempotent RPC is that a second person must not be able to
// start a second tick-loop; a control that only remembered ITS OWN click would show them a
// fresh, inviting button.

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { AlertTriangle, Loader2, Play, RotateCcw, Undo2 } from "lucide-react";
import { useReportingAccess } from "@/hooks/investment-reporting/useReportingAccess";
import { useReportingCycle } from "@/hooks/investment-reporting/useReportingCycle";
import {
  useEnqueueReportingCycle,
  useReportingCycleRun,
} from "@/hooks/investment-reporting/useReportingCycleRun";
import {
  useReopenReportingCycle,
  REOPEN_REASON_MIN_CHARS,
} from "@/hooks/investment-reporting/useReopenReportingCycle";
import {
  useRestartReportingCycle,
  useRestartReportingCyclePreview,
  RESTART_DEPTHS,
  RESTART_REASON_MIN_CHARS,
  type RestartDepth,
} from "@/hooks/investment-reporting/useRestartReportingCycle";
import { statusLabel, statusTone, toneDot } from "./labels";

// The statuses at which the worker's TERMINAL SHORT-CIRCUIT fires: it returns
// {complete:true, terminal:true} without doing any work. Queueing a run against one of these
// is not harmful, it is simply futile — and a control that lets someone do it and then shows
// nothing happening is worse than one that says why.
const TERMINAL_STATUSES = ["delivered", "failed", "cancelled"];

// The cursor's full path, in order. Shown as a strip so "where are we" is answerable at a
// glance rather than inferred from one word.
const STAGES = ["snapshot", "compute", "verify", "render", "gate", "deliver"] as const;

// research_job_runner_every_minute — the pg_cron entry that invokes the runner. The worker
// advances ONE step per invocation, so a five-stage walk is a five-to-ten minute affair and
// a spinner alone reads as a hang. This constant is what lets the UI say "next tick due in
// ~Ns" instead of spinning silently; if the cron schedule changes, change it here too.
const RUNNER_TICK_SECONDS = 60;

/** Local 1s clock so the "last moved / next tick" counters actually count. */
function useNowTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function secondsSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

// Of those three, only 'failed' is REOPENABLE. reopen_reporting_cycle refuses a delivered
// cycle outright (a restatement is a new cycle, not a reopened one) and refuses anything that
// is not terminal. Splitting this out matters because the advice differs: a failed cycle needs
// a reopen, a delivered one needs a new cycle, and telling an operator to visit the Gate tab —
// as this control used to — is wrong for both.
const REOPENABLE_STATUSES = ["failed"];

// RESTART is offered for EVERY status except delivered, which is deliberately wider than
// REOPENABLE_STATUSES above and is the whole reason 0003q is a separate RPC. A cycle that
// finished a stage successfully but on the wrong numbers or the wrong prose (status verified,
// gate unsigned) had NO control at all: reopen_reporting_cycle refuses a non-failed status
// outright (0003p check 6), so the only way to make such a cycle re-derive was direct DML,
// which leaves the permanent audit record describing a run that never happened.
// 'delivered' is excluded here as courtesy only. The server refuses it twice over, on the
// status AND on the delivery receipt, and the receipt is the one that matters.
const RESTARTABLE_STATUSES = ["draft", "computed", "verified", "rendered", "gated", "failed"];

// One line per depth, in the operator's words rather than the schema's. The AUTHORITATIVE
// account of what each depth clears is the server's plan, rendered below from the preview.
// These only help someone choose which depth to ask about.
const DEPTH_BLURB: Record<RestartDepth, string> = {
  render: "Re-render and re-freeze. The numbers and the prose stand.",
  narrative: "Re-author the prose. The frozen inputs and the computed numbers stand.",
  verify: "Re-assert the reconciliation ledger, then re-author the prose. Same effect as narrative today.",
  compute: "Re-derive the numbers against the same frozen inputs. Deletes the reconciliation ledger.",
  snapshot: "Re-freeze the inputs, then everything after. The deepest restart there is.",
};

export interface RunCycleControlProps {
  cycleId: string;
  periodLabel?: string;
  /** reporting_cycles.status — shown so the operator watches real state, not a spinner. */
  status?: string;
  /** reporting_cycles.current_stage — the cursor the run advances. */
  currentStage?: string;
  /** reporting_cycles.tick_count — worker invocations spent on the CURRENT stage. */
  tickCount?: number | null;
  /** reporting_cycles.stage_cap — the tick ceiling that fails a wedged cycle. */
  stageCap?: number | null;
  /** reporting_cycles.stage_outputs — per-stage progress the worker records as it goes. */
  stageOutputs?: Record<string, any> | null;
}

export function RunCycleControl({
  cycleId,
  periodLabel,
  status,
  currentStage,
  tickCount,
  stageCap,
  stageOutputs,
}: RunCycleControlProps) {
  const access = useReportingAccess();
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [lastQueuedJobId, setLastQueuedJobId] = useState<string | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [restartOpen, setRestartOpen] = useState(false);
  const [restartDepth, setRestartDepth] = useState<RestartDepth | null>(null);
  const [restartReason, setRestartReason] = useState("");
  const [restartAck, setRestartAck] = useState("");

  const run = useReportingCycleRun(cycleId, access.canReview);
  const enqueue = useEnqueueReportingCycle();
  const reopen = useReopenReportingCycle();
  const restart = useRestartReportingCycle();
  const restartPreview = useRestartReportingCyclePreview(cycleId, restartDepth, restartOpen);

  const runState = run.data;
  const inFlight = Boolean(runState?.in_flight);

  // Second observer on the SAME query key as the parent's useReportingCycle, subscribed in
  // live mode while a run is in flight. react-query dedupes by key and applies the shortest
  // refetchInterval among observers, so this makes the cursor refresh on the run row's 5s
  // beat without the parent needing to know a run is happening. The props are the parent's
  // (possibly 2-minute-stale) copy; prefer this one whenever it has landed — a live spinner
  // over a stale cursor is exactly what made a working run look hung.
  //
  // EVERY HOOK ABOVE THE ACCESS RETURN. The early return below is conditional, so a hook
  // placed after it would change call order between renders.
  const liveCycle = useReportingCycle(cycleId, inFlight);
  const now = useNowTicker(inFlight);

  // Access is gated at the island shell too; this is the second, local assertion so the
  // component can never be mounted somewhere less careful and quietly offer a spend action.
  // The RPC is the actual wall — it re-asserts can_review_reporting() server-side.
  if (access.loading || !access.canReview) return null;

  const stage = liveCycle.data?.current_stage ?? currentStage;
  const liveStatus = liveCycle.data?.status ?? status;
  const ticks = (liveCycle.data as any)?.tick_count ?? tickCount;
  const cap = (liveCycle.data as any)?.stage_cap ?? stageCap;
  const outputs = (liveCycle.data as any)?.stage_outputs ?? stageOutputs;
  const sinceRunUpdate = secondsSince(runState?.updated_at, now);
  // The runner fires on a fixed minute boundary, so time-to-next-tick is the remainder of
  // the cadence, not a countdown we control. Stated as "up to" for that reason.
  const nextTickIn =
    sinceRunUpdate === null ? null : Math.max(0, RUNNER_TICK_SECONDS - (sinceRunUpdate % RUNNER_TICK_SECONDS));
  const stageIndex = STAGES.indexOf(String(stage ?? "") as (typeof STAGES)[number]);

  // What the worker has actually recorded for the stage it is on. This is the difference
  // between "something is happening" and "reconstruct is done, attribute is next".
  const currentStageOutput =
    stage && outputs && typeof outputs === "object" ? (outputs as Record<string, any>)[stage] : null;
  const stageFacts: string[] =
    currentStageOutput && typeof currentStageOutput === "object"
      ? Object.entries(currentStageOutput)
          .filter(([, v]) => v !== null && v !== undefined && v !== false)
          .slice(0, 6)
          .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 40) : String(v)}`)
      : [];
  const isTerminal = TERMINAL_STATUSES.includes(String(liveStatus ?? ""));
  const isReopenable = REOPENABLE_STATUSES.includes(String(liveStatus ?? ""));
  const isRestartable = RESTARTABLE_STATUSES.includes(String(liveStatus ?? ""));
  const tone = statusTone(liveStatus);
  const reasonTooShort = reopenReason.trim().length < REOPEN_REASON_MIN_CHARS;

  // Restart gating. Every clause is the SERVER's answer, read back from the preview, except
  // the two purely local ones (a depth has been chosen, the reason is long enough). The
  // acknowledgement must match the server string exactly; it is not a checkbox, because a
  // checkbox can be ticked without reading and an echoed fact cannot.
  const rp = restartPreview.data;
  const restartReasonTooShort = restartReason.trim().length < RESTART_REASON_MIN_CHARS;
  const ackNeeded = Boolean(rp?.ack_required);
  const ackMismatch = ackNeeded && restartAck.trim() !== (rp?.ack_string ?? "");
  const restartBlocked =
    !restartDepth ||
    restartPreview.isLoading ||
    restartPreview.isError ||
    !rp ||
    !rp.can_restart ||
    restartReasonTooShort ||
    ackMismatch;

  // A parked job: the <job_type>_paused feature-flag trigger pushes next_run_at ~30 days out.
  // Without surfacing this, a paused run reads as a permanently "queued" job with no reason.
  const nextRunAt = runState?.next_run_at ? new Date(runState.next_run_at) : null;
  const parked = inFlight && nextRunAt != null && nextRunAt.getTime() > Date.now() + 60 * 60 * 1000;

  async function confirmRun() {
    if (!acknowledged || inFlight) return;
    try {
      const jobId = await enqueue.mutateAsync(cycleId);
      setLastQueuedJobId(jobId ?? null);
      setOpen(false);
    } catch {
      /* surfaced from enqueue.error below — never a silent failure */
    }
  }

  function openChange(v: boolean) {
    setOpen(v);
    if (v) {
      setAcknowledged(false);
      enqueue.reset();
    }
  }

  async function confirmReopen() {
    if (reasonTooShort) return;
    try {
      await reopen.mutateAsync({ cycleId, reason: reopenReason.trim() });
      setReopenOpen(false);
      setReopenReason("");
    } catch {
      /* surfaced from reopen.error below — the RPC's own words, never a paraphrase */
    }
  }

  function reopenOpenChange(v: boolean) {
    setReopenOpen(v);
    if (v) reopen.reset();
  }

  async function confirmRestart() {
    if (restartBlocked || !restartDepth) return;
    try {
      await restart.mutateAsync({
        cycleId,
        depth: restartDepth,
        reason: restartReason.trim(),
        ack: ackNeeded ? restartAck.trim() : null,
      });
      setRestartOpen(false);
      setRestartReason("");
      setRestartAck("");
      setRestartDepth(null);
    } catch {
      /* surfaced from restart.error below: the RPC's own words, never a paraphrase */
    }
  }

  function restartOpenChange(v: boolean) {
    setRestartOpen(v);
    if (v) {
      restart.reset();
      // NEVER pre-fill the reason, not even from a previous restart on the same cycle. The
      // approvals log is append-only and its reason text can never be corrected, so a
      // pre-filled sentence would be a permanent claim nobody wrote on purpose.
      setRestartReason("");
      setRestartAck("");
      setRestartDepth(null);
    }
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 space-y-1">
          <h4 className="font-heading text-sm font-medium text-foreground">Run the cycle</h4>
          <p className="font-body text-xs text-muted-foreground max-w-2xl">
            Queues one run of the pipeline for this cycle. The worker advances the cursor one
            stage per tick and stops at the gate for a human signature — it never delivers
            anything unsigned.
          </p>
          {/* Honest state, straight off the cycle row. */}
          <p className="font-body text-xs text-muted-foreground">
            Now at{" "}
            <span className="font-medium text-foreground">{stage ?? "—"}</span>
            <span className="mx-1.5 text-muted-foreground/50">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className={cn("h-1.5 w-1.5 rounded-full", toneDot(tone))} aria-hidden />
              {statusLabel(liveStatus)}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
        {/* ── Reopen: the CONTROL act. Shown only for a failed cycle, because that is the
            only status reopen_reporting_cycle accepts. It deliberately does not queue a run —
            reopening and spending are two decisions, and 0003p keeps them apart. ────────── */}
        {isReopenable && (
          <Dialog open={reopenOpen} onOpenChange={reopenOpenChange}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                Reopen
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  Reopen {periodLabel ? `the ${periodLabel} cycle` : "this cycle"}?
                </DialogTitle>
                <DialogDescription className="font-body">
                  This clears the recorded failure and lets the pipeline run again. It does not
                  run anything by itself.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 font-body text-sm">
                <p className="text-xs text-muted-foreground">
                  The cycle resumes from{" "}
                  <span className="font-medium text-foreground">{stage ?? "its current stage"}</span>{" "}
                  rather than starting over, and its status returns to whatever matches that
                  stage. Completed work is not thrown away, and the reconciliation verdicts from
                  the failed attempt are copied into the audit row before anything is overwritten.
                </p>

                {/* THE SENTENCE THAT WAS MISSING, and its absence is what made 2026-08-17
                    surprising. "Completed work is not thrown away" is true of a reopen and it
                    is also the trap: the completion MARKERS survive too, so a stage that
                    already finished will not run again however much its code or prompt was
                    fixed in the meantime. A resume of cycle ec8e622a after a real prompt fix
                    produced byte-identical prose for exactly this reason. */}
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  What a reopen does NOT do: the completion markers survive. Any stage that has
                  already finished will be skipped again, so a fix to its code or its prompt will
                  not be exercised. If a stage needs to re-derive, use Restart and pick the depth.
                </p>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground" htmlFor="reopen-reason">
                    Why is this being reopened?
                  </label>
                  <Textarea
                    id="reopen-reason"
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    rows={4}
                    placeholder="State what changed since the failure and why the cycle should now be expected to get further."
                    className="text-xs"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Minimum {REOPEN_REASON_MIN_CHARS} characters, enforced by the server.{" "}
                    {reasonTooShort && reopenReason.length > 0 && (
                      <span className="text-amber-600 dark:text-amber-400">
                        {REOPEN_REASON_MIN_CHARS - reopenReason.trim().length} more to go.
                      </span>
                    )}{" "}
                    This text is written to the append-only approvals log against your name and
                    CANNOT be edited afterwards.
                  </p>
                </div>

                {reopen.isError && (
                  <p className="text-xs text-destructive whitespace-pre-wrap">
                    {(reopen.error as any)?.message ?? "The cycle could not be reopened."}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setReopenOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={confirmReopen} disabled={reasonTooShort || reopen.isPending}>
                  {reopen.isPending ? "Reopening…" : "Reopen the cycle"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* ── Restart: the RE-DERIVE act (0003q). Shown for EVERY status except delivered,
            including the successful ones, because that is the case with no control today:
            reopen_reporting_cycle refuses a non-failed status outright, so a cycle that
            finished on the wrong prose had to be edited by hand. Adjacent to Run and Reopen on
            purpose, so the same server-truth in-flight state gates all three. Like reopen it
            does NOT queue a run: 0003q keeps 0003p's control-versus-spend separation. ─────── */}
        {isRestartable && (
          <Dialog open={restartOpen} onOpenChange={restartOpenChange}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0">
                <Undo2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                Restart
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  Restart {periodLabel ? `the ${periodLabel} cycle` : "this cycle"} from a chosen
                  stage?
                </DialogTitle>
                <DialogDescription className="font-body">
                  A restart rewinds the cursor and DISCARDS the completion markers behind it, so
                  those stages derive again from scratch. It does not run anything by itself.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 font-body text-sm">
                {/* 1. Choose the depth. Named by stage, never by a number. */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-foreground">
                    How far back? Shallowest first.
                  </p>
                  <div className="space-y-1">
                    {RESTART_DEPTHS.map((d) => (
                      <label
                        key={d}
                        className={cn(
                          "flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer",
                          restartDepth === d ? "border-primary bg-primary/5" : "border-border",
                        )}
                      >
                        <input
                          type="radio"
                          name="restart-depth"
                          className="mt-1"
                          checked={restartDepth === d}
                          onChange={() => {
                            setRestartDepth(d);
                            // The acknowledgement string is depth-specific, so a token typed
                            // for one depth must never satisfy another.
                            setRestartAck("");
                          }}
                        />
                        <span className="min-w-0">
                          <span className="font-mono text-[11px] text-foreground">{d}</span>
                          <span className="block text-[11px] text-muted-foreground">
                            {DEPTH_BLURB[d]}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 2. What the SERVER says will happen. Nothing in this block is hand-written:
                       a hand-written sentence about what a control discards is exactly how the
                       Reopen dialog came to mislead. */}
                {restartDepth && restartPreview.isLoading && (
                  <p className="text-xs text-muted-foreground">
                    Reading what this would actually clear…
                  </p>
                )}

                {restartDepth && restartPreview.isError && (
                  <p className="text-xs text-destructive whitespace-pre-wrap">
                    {(restartPreview.error as any)?.message ??
                      "Could not read the restart preview."}{" "}
                    Restart is disabled while that is unknown rather than guessing what would be
                    discarded.
                  </p>
                )}

                {rp && (
                  <div className="space-y-3 rounded-md bg-muted/40 px-3 py-2.5">
                    <p className="text-xs text-foreground">
                      Cursor moves{" "}
                      <span className="font-mono">{rp.current_stage}</span> to{" "}
                      <span className="font-mono font-medium">{rp.plan.target_stage}</span>, status{" "}
                      <span className="font-mono">{rp.status}</span> to{" "}
                      <span className="font-mono font-medium">{rp.plan.new_status}</span>.
                    </p>
                    <p className="text-[11px] text-muted-foreground">{rp.plan.note}</p>

                    {/* WHAT WILL BE DISCARDED, by name. */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-foreground">
                        What will be discarded
                      </p>
                      <ul className="space-y-0.5 text-[11px] text-muted-foreground list-disc pl-4">
                        <li>
                          completion markers:{" "}
                          {rp.will_clear.stage_outputs_keys.length === 0 ? (
                            <span>none of the keys this depth clears are present on this cycle</span>
                          ) : (
                            <span className="font-mono text-foreground">
                              stage_outputs.{rp.will_clear.stage_outputs_keys.join(", stage_outputs.")}
                            </span>
                          )}
                        </li>
                        <li>
                          derived IR:{" "}
                          {rp.will_clear.ir_subtrees.length === 0 ? (
                            <span>nothing</span>
                          ) : (
                            <span className="font-mono text-foreground">
                              {rp.will_clear.ir_subtrees.join(", ")}
                            </span>
                          )}
                        </li>
                        <li>
                          reconciliation ledger:{" "}
                          {rp.will_clear.reconciliation_rows === 0 ? (
                            <span>kept in place (nothing deleted)</span>
                          ) : (
                            <span className="text-foreground">
                              {rp.will_clear.reconciliation_rows} row
                              {rp.will_clear.reconciliation_rows === 1 ? "" : "s"} DELETED, after
                              every row is copied whole into the audit row
                            </span>
                          )}
                        </li>
                        <li>
                          frozen inputs:{" "}
                          {rp.will_clear.snapshot_ref ? (
                            <span className="text-foreground">
                              snapshot_ref CLEARED. A new snapshot freezes whatever is attested at
                              the moment it runs, so this is not a reproduction of the earlier
                              input set.
                            </span>
                          ) : (
                            <span>kept. The re-run uses the same frozen inputs.</span>
                          )}
                        </li>
                        <li>the recorded failure text, and the tick count back to 0</li>
                      </ul>
                    </div>

                    {/* WHAT WILL NOT CHANGE. Same register: named, not implied. */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-foreground">What will NOT change</p>
                      <ul className="space-y-0.5 text-[11px] text-muted-foreground list-disc pl-4">
                        <li>
                          golden CSV rows and every golden-source attestation, at any depth
                        </li>
                        <li>
                          the approvals log. It is append-only: this restart appends one row and
                          can neither edit nor delete an earlier one, including its own reason
                        </li>
                        <li>
                          frozen artifact version rows. They are never deleted and cannot be
                        </li>
                        <li>anything already delivered</li>
                        <li>
                          the gate. <span className="font-mono">gate_approved</span>,{" "}
                          <span className="font-mono">approved_by</span> and{" "}
                          <span className="font-mono">approved_at</span> are never written by a
                          restart at any depth
                        </li>
                        <li>
                          <span className="font-mono">stage_seq</span> ({rp.stage_seq}). It is the
                          worker&rsquo;s lost-write token, not a progress counter, so resetting it
                          would let a stale in-flight tick overwrite this act
                        </li>
                      </ul>
                    </div>

                    {/* The frozen-artifact inventory, itemised. "Stranded" is a real word with a
                        real consequence, so it is spelled out rather than implied. */}
                    {rp.artifact_versions.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-foreground">
                          Frozen artifacts on this cycle
                        </p>
                        <ul className="space-y-0.5">
                          {rp.artifact_versions.map((v) => (
                            <li
                              key={v.version_id}
                              className="font-mono text-[10px] text-muted-foreground break-all"
                            >
                              {v.kind} · {v.artifact_sha256.slice(0, 12)} ·{" "}
                              {v.bound_to_current_ir
                                ? rp.plan.moves_ir
                                  ? "bound to the current IR, will be STRANDED"
                                  : "bound to the current IR, stays bindable"
                                : "already unbound from the current IR"}
                            </li>
                          ))}
                        </ul>
                        {rp.plan.moves_ir && (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400">
                            Stranded means the bytes and the version rows stay forever, because
                            they cannot be deleted, but the gate binds a signature only to
                            artifacts whose IR hash matches. Once this depth moves the IR they can
                            never satisfy a gate again.
                          </p>
                        )}
                      </div>
                    )}

                    <p className="text-[11px] text-muted-foreground">
                      Restart {rp.restart_ordinal} of {rp.controls_budget} on this cycle (
                      {rp.controls_used} reopen or restart acts used so far).{" "}
                      <span className="font-mono">stage_seq</span> headroom{" "}
                      {rp.stage_seq_headroom} against{" "}
                      {rp.walk_cost_measured === null
                        ? `a ${rp.walk_cost_required}-stage floor, because no walk has been measured on this cycle yet`
                        : `${rp.walk_cost_measured}, which is what this cycle's last walk actually cost`}
                      .
                    </p>
                  </div>
                )}

                {/* 3. Server refusals, in the server's words. */}
                {rp && rp.refusals.length > 0 && (
                  <div className="space-y-1.5">
                    {rp.refusals.map((r) => (
                      <div
                        key={r.code}
                        className="flex items-start gap-1.5 text-xs text-destructive"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                        <span>
                          <span className="font-mono text-[10px]">
                            {r.tier === 1 ? "refused" : "blocked"} · {r.code}
                          </span>
                          <span className="block">{r.detail}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* 4. The reason. Append-only, never pre-filled. */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground" htmlFor="restart-reason">
                    Why must these stages re-derive rather than resume?
                  </label>
                  <Textarea
                    id="restart-reason"
                    value={restartReason}
                    onChange={(e) => setRestartReason(e.target.value)}
                    rows={4}
                    placeholder="State what changed, and why the affected stages will now produce something different."
                    className="text-xs"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Minimum {RESTART_REASON_MIN_CHARS} characters, enforced by the server.{" "}
                    {restartReasonTooShort && restartReason.length > 0 && (
                      <span className="text-amber-600 dark:text-amber-400">
                        {RESTART_REASON_MIN_CHARS - restartReason.trim().length} more to go.
                      </span>
                    )}{" "}
                    This text is written to the append-only approvals log against your name and
                    CANNOT be edited afterwards.
                  </p>
                </div>

                {/* 5. The tier-2 acknowledgement. A TEXT field, deliberately not a checkbox:
                       a checkbox can be ticked without reading, an echoed fact cannot. The
                       string is computed by the server and compared by the server. */}
                {ackNeeded && (
                  <div className="space-y-1.5 rounded-md border border-amber-500/40 px-3 py-2.5">
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                      This cycle carries frozen artifacts bound to its current IR
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      To confirm you have read what happens to them, type this exactly:
                    </p>
                    <p className="font-mono text-[11px] text-foreground break-all select-all">
                      {rp?.ack_string}
                    </p>
                    <Input
                      value={restartAck}
                      onChange={(e) => setRestartAck(e.target.value)}
                      placeholder="Type the line above"
                      className="text-xs font-mono"
                    />
                    {ackMismatch && restartAck.length > 0 && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        Not an exact match yet. The server compares against its own copy.
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Restarting does not queue a run. Nothing is spent until you use Run cycle
                  afterwards, which is a separate decision.
                </p>

                {restart.isError && (
                  <p className="text-xs text-destructive whitespace-pre-wrap">
                    {(restart.error as any)?.message ?? "The cycle could not be restarted."}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setRestartOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={confirmRestart}
                  disabled={restartBlocked || restart.isPending}
                >
                  {restart.isPending
                    ? "Restarting…"
                    : restartDepth
                      ? `Restart from ${restartDepth}`
                      : "Choose a depth"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <Dialog open={open} onOpenChange={openChange}>
          <DialogTrigger asChild>
            {/* Disabled while the run state is UNKNOWN (loading or errored) as well as while a
                run is live: offering to start a run when we cannot see whether one is already
                going is exactly how a second tick-loop gets created. */}
            <Button size="sm" disabled={inFlight || run.isLoading || run.isError} className="shrink-0">
              {inFlight ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden />
                  Run in flight
                </>
              ) : (
                <>
                  <Play className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                  Run cycle
                </>
              )}
            </Button>
          </DialogTrigger>

          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Run {periodLabel ? `the ${periodLabel} cycle` : "this reporting cycle"}?
              </DialogTitle>
              <DialogDescription className="font-body">
                This starts the pipeline for real and costs money. Read what it will do before
                you confirm.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 font-body text-sm">
              <div className="space-y-1.5">
                <p className="text-foreground">Starting from {stage ?? "the current stage"}, it will:</p>
                <ul className="space-y-1 text-xs text-muted-foreground list-disc pl-4">
                  <li>
                    <span className="text-foreground">snapshot</span> — freeze the golden rows
                    already dropped on this cycle. No charge.
                  </li>
                  <li>
                    <span className="text-foreground">compute</span> — run the attribution and
                    reconciliation engine over them. <span className="text-foreground">Billable.</span>
                  </li>
                  <li>
                    <span className="text-foreground">verify</span> — write the reconciliation
                    ledger and raise flags. No charge.
                  </li>
                  <li>
                    <span className="text-foreground">render</span> — write the LLM narrative and
                    freeze the decks and newsletter.{" "}
                    <span className="text-foreground">Billable — this is the expensive leg.</span>
                  </li>
                  <li>
                    <span className="text-foreground">gate</span> — and STOP. The run parks here
                    unless the gate is already signed on the Gate tab.
                  </li>
                </ul>
              </div>

              <p className="text-xs text-muted-foreground">
                Nothing is emailed. Delivery only runs on a cycle whose gate a named human has
                signed, and newsletter auto-send is separately disabled by default — a run
                cannot send to clients on its own.
              </p>

              {isTerminal && (
                <div className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                  <span>
                    This cycle is {statusLabel(liveStatus).toLowerCase()}, so the worker will
                    self-terminate on the first tick without doing any work.{" "}
                    {isReopenable ? (
                      <>
                        Reopen it first, using the Reopen button next to Run. That clears the
                        failure and resumes from{" "}
                        <span className="font-medium">{stage ?? "the current stage"}</span>{" "}
                        rather than starting over, and it KEEPS every completion marker, so a
                        stage that already finished will be skipped again. If a stage needs to
                        re-derive after a code or prompt fix, use Restart and pick the depth.
                      </>
                    ) : (
                      <>
                        It cannot be reopened: a delivered cycle&rsquo;s outputs are already in
                        recipients&rsquo; hands, so a restatement is a NEW cycle, not a reopened
                        one.
                      </>
                    )}
                  </span>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Only one run can be in flight per cycle: if one is already queued or running you
                get that job back rather than a second one. Two parallel runs would double-write
                the reconciliation ledger.
              </p>

              <label className="flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I understand this queues a real run that will spend money on the compute and
                  render legs.
                </span>
              </label>

              {enqueue.isError && (
                <p className="text-xs text-destructive">
                  {(enqueue.error as any)?.message ?? "The run could not be queued."}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={confirmRun} disabled={!acknowledged || inFlight || enqueue.isPending}>
                {enqueue.isPending ? "Queueing…" : "Queue the run"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* ── Run state, read from the server ─────────────────────────────────── */}
      {run.isError && (
        <p className="font-body text-xs text-destructive">
          Could not read this cycle&rsquo;s run state:{" "}
          {(run.error as any)?.message ?? "unknown error"}. The Run button is disabled while the
          state is unknown rather than risking a duplicate run.
        </p>
      )}

      {runState?.job_id && (
        <div className="rounded-md bg-muted/40 px-3 py-2 space-y-1">
          <p className="font-body text-xs text-foreground">
            {inFlight ? "Run in flight" : "Last run"} —{" "}
            <span className="font-medium">{runState.status ?? "—"}</span>
            {typeof runState.attempts === "number" && runState.attempts > 0 && (
              <span className="text-muted-foreground">
                {" "}
                (attempt {runState.attempts}
                {typeof runState.max_attempts === "number" ? ` of ${runState.max_attempts}` : ""})
              </span>
            )}
          </p>
          <p className="font-body text-[11px] text-muted-foreground font-mono break-all">
            job {runState.job_id}
            {runState.job_id === lastQueuedJobId ? " — queued by you just now" : ""}
          </p>
          {parked && (
            <p className="font-body text-[11px] text-amber-600 dark:text-amber-400">
              Parked until {nextRunAt?.toLocaleString()} — a{" "}
              <span className="font-mono">run_reporting_cycle_paused</span> feature flag is
              holding this job back. Clear the flag to release it.
            </p>
          )}
          {runState.last_error && (
            <p className="font-body text-[11px] text-destructive break-words">
              Job error: {runState.last_error}
            </p>
          )}
          {inFlight && (
            <div className="space-y-2 pt-1">
              {/* The cursor's position, spelled out. A run is a WALK, and the walk is the
                  progress — not the spinner. */}
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                {STAGES.map((s, i) => {
                  const done = stageIndex >= 0 && i < stageIndex;
                  const here = i === stageIndex;
                  return (
                    <span key={s} className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          "font-mono text-[10px] px-1.5 py-0.5 rounded",
                          here && "bg-primary/15 text-primary font-medium",
                          done && "text-muted-foreground line-through decoration-muted-foreground/40",
                          !here && !done && "text-muted-foreground/50",
                        )}
                      >
                        {s}
                      </span>
                      {i < STAGES.length - 1 && (
                        <span className="text-muted-foreground/30 text-[10px]" aria-hidden>
                          ›
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>

              {stageIndex >= 0 && (
                <p className="font-body text-[11px] text-muted-foreground">
                  Step {stageIndex + 1} of {STAGES.length}
                  {typeof ticks === "number" && (
                    <>
                      {" · "}tick {ticks}
                      {typeof cap === "number" ? ` of ${cap} allowed on this stage` : ""}
                    </>
                  )}
                </p>
              )}

              {/* The counters that make waiting legible. Without these the page cannot
                  distinguish "between ticks" from "wedged". */}
              <p className="font-body text-[11px] text-muted-foreground tabular-nums">
                Last movement {sinceRunUpdate === null ? "—" : `${sinceRunUpdate}s ago`}
                {nextTickIn !== null && <> · next tick in up to {nextTickIn}s</>}
              </p>

              {stageFacts.length > 0 && (
                <ul className="space-y-0.5">
                  {stageFacts.map((f) => (
                    <li key={f} className="font-mono text-[10px] text-muted-foreground break-all">
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              <p className="font-body text-[11px] text-muted-foreground">
                The runner is a one-minute cron and the worker advances ONE step per
                invocation, so a full walk takes several minutes. A still spinner between
                ticks is normal — watch the counter above, not the spinner.
              </p>
            </div>
          )}
        </div>
      )}

      {!runState?.job_id && !run.isLoading && !run.isError && (
        <p className="font-body text-[11px] text-muted-foreground">
          No run has ever been started for this cycle.
        </p>
      )}
    </Card>
  );
}
